import { useState, useRef, useEffect, useId } from 'react';
import { Box } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropFreeIcon from '@mui/icons-material/CropFree';
import {
  boundingBox,
  fitScale,
  polygonToLocal,
  polygonToPathD,
  placementToLocalRect,
  polygonCentroid,
  insetRectilinearPolygon,
  toLocalPoint,
} from './geometry2';
import { GREEN, CHARCOAL, BORDER } from '../../../theme/tokens2';

// ─── FloorplanCanvas ────────────────────────────────────────────────────────
// Draws every room in a combination as its true footprint polygon (NOT a
// bounding rect -- rooms may be L-shaped or otherwise non-rectangular),
// with real wall segments drawn on top (gaps in the segment list are
// doorways/openings and are intentionally left undrawn), and every
// selected layout's furniture placements composited inside, each rotated
// individually about its own start point.
//
// Logical canvas size -- TWO DIFFERENT STRATEGIES depending on `interactive`:
//
//   - interactive === true (the full workspace canvas): the canvas fills
//     whatever real pixel box its parent panel gives it (measured live via
//     ResizeObserver, see `measuredBox`), full stop. It does NOT shrink
//     itself to match the apartment's own aspect ratio. This used to be a
//     bug -- a wide/shallow apartment would render a short SVG box inside
//     a tall fixed-height rail panel, leaving awkward dead space below it.
//     Now the box always fills the panel; any mismatch between the panel's
//     aspect ratio and the apartment's becomes real letterboxing (see
//     offsetX/offsetY), computed against the ACTUAL rendered box rather
//     than against the content.
//   - interactive === false (comparison-card thumbnails): unchanged from
//     before -- the box shrinks to match the apartment's own aspect ratio
//     (see canvasContentSize), since a card has no fixed panel to fill and
//     letterboxing there would just look like unwanted padding.
//
// Zoom + pan:
// Zoom is uncapped on the high end (MIN_ZOOM=1 is still a floor -- you
// can't zoom out past "fit to screen"). Because the panel-fill strategy
// above means the SVG always occupies a real, fixed pixel box, zooming in
// indefinitely is safe: content that grows past the edges of that box is
// simply clipped by the SVG's own viewBox (an explicit `overflow: hidden`
// makes this cropping behaviour intentional rather than incidental), the
// same way a real design-tool canvas crops an over-zoomed layer instead of
// refusing to zoom further.
// Both zoom directions are anchored to a fixed screen point rather than
// the SVG's origin corner -- zooming via the buttons anchors to the
// canvas's own center, zooming via the scroll wheel anchors under the
// cursor (the Figma/Miro convention). Anchoring matters: naively scaling
// around (0,0) makes the content fly toward the bottom-right corner and
// out of the viewBox the moment you zoom in, which reads as the drawing
// getting "cropped" in the wrong way. Dragging to pan converts real screen
// pixels into viewBox units using the SVG element's actual rendered size
// (via svgRef), since those two units are NOT 1:1 whenever the SVG is
// displayed at a different pixel size than its logical viewBox -- which
// is always, in practice.
//
// Dimension ruler -- a sliding tape measure, not a fixed coordinate grid:
// Rendered as a Ruler component, OUTSIDE the pan/zoom transform group.
// The ruler bars themselves are affixed to the canvas edges and never
// move, but zero is deliberately NOT pinned to any fixed point in the
// apartment (unlike Figma/Illustrator/CAD-style rulers, which anchor zero
// to the document). Instead, zero is always the bar's own near edge --
// left for the horizontal bar, top for the vertical one -- and labels
// count outward from whatever real-world point currently sits there.
// Panning the floorplan is how the user re-zeros it: drag a point of
// interest to the edge, then read the distance to anything else directly
// off the far end, no subtraction needed -- the literal "hook the tape
// on this wall, read the other end" workflow (see buildSlidingTicks). A
// center-mirrored version was tried and reverted first (it forced adding
// instead of subtracting labels across the center); a document-anchored
// version was tried and reverted second (it's the right convention for a
// shared coordinate system, but this tool has no such system to keep
// stable -- the actual task is always "how far apart are these two
// things," which the sliding zero answers with no arithmetic at all).
// Ticks still run the FULL length of each bar, continuing out past the
// apartment's own edge into whatever letterboxed margin exists, rather
// than stopping dead at the floorplan's boundary.
//
// Used both as the large, clickable canvas inside the active
// arrangement's workspace, and -- at a much smaller scale,
// non-interactive -- inside each comparison card.
//
// Props:
//   combination        — a RoomCombinationDto-shaped object (rooms[], totalWidthMm, totalHeightMm)
//   furniturePieces     — array of FurniturePieceDto, the global stencil catalog (from GET /furniture-pieces)
//   selectedRoomLabel   — label of the currently-selected room (full-size mode only), or null
//   onRoomClick         — fn(roomLabel) called when a room is clicked; omit for non-interactive thumbnails
//   interactive         — boolean; when false, rooms have no hover/selected affordance, no labels,
//                          no completion badges, no click handler, and no cursor override (inherits
//                          whatever cursor its container set, e.g. 'pointer' on a clickable card).
//                          Also selects which of the two sizing strategies above applies.
//   maxHeight           — optional desired height (number or responsive sx object). When interactive,
//                          this is applied directly as the wrapper's real CSS height, which the
//                          ResizeObserver then measures to fill (panel-fill strategy). When not
//                          interactive, it's instead converted into an equivalent maxWidth via the
//                          drawing's own aspect ratio (see `widthCap`) -- capping height directly
//                          there can mismatch the rendered box against the viewBox, since height is
//                          computed FROM width in that (shrink-to-content) strategy.
//   showDimensions      — boolean; draws the affixed width/height ruler. Defaults to `interactive` --
//                          comparison-card thumbnails are too small for this to be legible or useful.
//   showZoomControls    — boolean; shows a zoom-in/zoom-out/fit-to-screen button cluster and enables
//                          wheel-to-zoom + drag-to-pan. Defaults to `interactive`.

// The apartment's longer side always maps to this many logical units; the
// shorter side is derived proportionally (see canvasContentSize), so the
// logical canvas always matches each apartment's real aspect ratio.
const TARGET_LONG_SIDE = 700;

// Reserved margin around the drawing on ALL FOUR sides when the ruler is
// shown -- the ruler bar itself only draws ticks in the bottom/left bands,
// but the same margin exists on top/right too, purely as balancing
// whitespace. Without this, reserving space only where the ruler actually
// needs it pushes the drawing itself toward the top-right corner instead
// of keeping it centered in the canvas.
const RULER_MARGIN = 40;

// Small margin reserved on ALL FOUR sides regardless of whether the ruler
// is shown -- walls are drawn exactly along a room's real boundary with a
// non-zero stroke width, so without any margin at all, a wall sitting on
// the apartment's outer edge has half its stroke clipped off by the
// canvas's own overflow:hidden. Added on top of RULER_MARGIN when the
// ruler IS shown (where it's negligible next to that much bigger margin),
// and is the ONLY margin when it isn't -- e.g. the compare-grid
// thumbnails, which is exactly where the clipping was visible before this
// existed.
const STROKE_BLEED_MARGIN = 4;

// Used for the centered label on a room with no room-uses at all, or a
// room currently showing as Empty (see the hatch-pattern/dashed-outline
// rendering below) -- the old per-room name/completion-badge labelling
// was removed everywhere else, since the Explore tab already shows the
// selected room's name once a real layout is chosen. This is the
// interactive-canvas size only -- see roomLabelFontSize below for why the
// compare grid needs a different value.
const ROOM_LABEL_FONT_SIZE = 11;

// The compare grid draws the same room at the same logical scale as the
// interactive canvas, but into a much smaller real pixel box (capped by
// `maxHeight`, typically ~180px) -- so a flat logical font size that
// reads fine on the big canvas would render as just a few real screen
// pixels there, effectively invisible. This ratio is multiplied by the
// canvas's own logicalHeight instead of using a fixed number, which
// works out to a CONSTANT real on-screen size regardless of a given
// room's aspect ratio: shrink-to-content sizing (see canvasContentSize)
// always ends up rendered at real height ~= maxHeight no matter what
// logicalHeight is, so logicalHeight and that compression cancel out.
// Calibrated for roughly a 9px real on-screen label at the one real
// non-interactive call site (CombinationCard4's maxHeight={180}) --
// 0.05 * 180 = 9.
const NON_INTERACTIVE_LABEL_FONT_RATIO = 0.05;

// Real-world inward offset (mm) for the dashed "Empty layout selected"
// outline -- matches the pipeline's own placementPerimeter buffer
// distance (see structure_dataEntry5.py) so the dashed line sits just
// inside the real walls without needing that value from the backend
// (see geometry2.js's insetRectilinearPolygon).
const EMPTY_OUTLINE_INSET_MM = 62;

// Hatch pattern for a room with no room-uses at all (nothing was matched
// for it in the FRUITS tab, so there's nothing to plan here yet).
const HATCH_SIZE = 8; // px, in logical/viewBox units
const HATCH_COLOR = 'rgba(0,0,0,0.28)';

// A floor, not a ceiling -- you can't zoom out past "fit to screen", but
// zooming IN is deliberately uncapped (see the header comment above on
// panel-fill sizing + clipping).
const MIN_ZOOM = 1;
const ZOOM_STEP = 0.35;

const MUTED_LINE = 'rgba(0,0,0,0.45)';

// Window openings get their own grey line, distinct from both the
// charcoal wall color and the ruler's MUTED_LINE, so a window reads as
// "a real feature" rather than either a wall or a UI element.
const WINDOW_COLOR = '#adc6df';

// Ruler tick spacing: candidate "nice" real-world step sizes (mm), the
// smallest of which still clears MIN_TICK_SPACING logical units on
// screen at the current effective scale (apartment scale x zoom).
const MIN_TICK_SPACING = 60;
const NICE_STEPS_MM = [50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000];

function canvasContentSize(apartmentWidth, apartmentHeight) {
  if (apartmentWidth >= apartmentHeight) {
    return [TARGET_LONG_SIDE, TARGET_LONG_SIDE * (apartmentHeight / apartmentWidth)];
  }
  return [TARGET_LONG_SIDE * (apartmentWidth / apartmentHeight), TARGET_LONG_SIDE];
}

function chooseTickStepMm(effectiveScale) {
  for (const step of NICE_STEPS_MM) {
    if (step * effectiveScale >= MIN_TICK_SPACING) return step;
  }
  return NICE_STEPS_MM[NICE_STEPS_MM.length - 1];
}

/**
 * Builds tick positions for a "sliding tape measure" ruler: zero is
 * always the ruler bar's own near edge (the panel's left edge for the
 * horizontal bar, top edge for the vertical one) -- NOT any fixed point
 * in the apartment. Panning the floorplan is how the user re-zeros it:
 * drag a point of interest to the edge, and whatever's at the far end
 * reads its distance directly, with no subtraction needed. This is the
 * literal "hook the tape on this wall, read the other end" workflow,
 * as opposed to a document-anchored ruler (Figma/Illustrator/CAD-style)
 * whose zero stays fixed to one point in the drawing regardless of pan.
 *
 * Ticks are generated by walking outward from 0 in steps of stepMm across
 * spanMm (the real-world mm distance currently visible along the whole
 * bar) -- so they always start exactly at the edge and always cover the
 * bar's full length, by construction, with no separate range-filtering
 * needed the way a fixed-origin ruler requires.
 */
function buildSlidingTicks(spanMm, stepMm) {
  const ticks = [];
  for (let offsetMm = 0; offsetMm <= spanMm + 0.001; offsetMm += stepMm) {
    ticks.push({ offsetMm, label: Math.round(offsetMm) });
  }
  return ticks;
}

/**
 * The room's currently-selected LayoutOptionDto, or null if nothing real
 * is selected -- which includes Empty (see the isEmptySelected note
 * below: Empty isn't a real layout option anymore, just the absence of
 * one), and a selection that points at a room-use/layout that no longer
 * exists. Shared by RoomFurniture (which needs the placements) and the
 * dashed "Empty" outline/label below.
 */
function getSelectedLayoutOption(room) {
  const selection = room.selection;
  if (!selection || !selection.selectedRoomUseName || !selection.selectedLayoutOptionId) return null;
  const roomUse = room.roomUses.find((u) => u.name === selection.selectedRoomUseName);
  return roomUse?.layoutOptions.find((l) => l.id === selection.selectedLayoutOptionId) || null;
}

export default function FloorplanCanvas({
  combination,
  furniturePieces,
  selectedRoomLabel = null,
  onRoomClick = null,
  interactive = true,
  maxHeight = null,
  showDimensions = interactive,
  showZoomControls = interactive,
}) {
  const pieceByName = Object.fromEntries((furniturePieces || []).map((p) => [p.name, p]));
  // Unique per canvas instance so multiple floorplans on the same page
  // (e.g. one per card in the comparison grid) never share a pattern id.
  const hatchId = `room-hatch-${useId()}`;

  const svgRef = useRef(null);
  const [view, setView] = useState({ zoom: 1, pan: { x: 0, y: 0 } });
  const [isDragging, setIsDragging] = useState(false);
  // Tracks an in-progress drag so a pan that actually moved the view can
  // suppress the room click that would otherwise fire on pointer-up.
  const dragState = useRef(null);

  // Panel-fill mode only: the wrapper div (NOT the svg) is given a real
  // CSS height by its parent (via `maxHeight`, or `height:'100%'` if the
  // parent itself stretches). We measure THAT box live, then use it
  // directly as the SVG's viewBox -- this is what lets the drawing fill
  // whatever real panel space exists instead of shrinking to match the
  // apartment's own aspect ratio. Measuring the wrapper rather than the
  // svg avoids circularity: the wrapper's size is set externally by CSS
  // and doesn't depend on the svg's content at all.
  const wrapperRef = useRef(null);
  const [measuredBox, setMeasuredBox] = useState(null);

  useEffect(() => {
    if (!interactive || !wrapperRef.current) return undefined;
    const el = wrapperRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setMeasuredBox((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [interactive]);

  const clampZoom = (z) => Math.max(MIN_ZOOM, z);

  // Zooms while keeping `anchor` (a point in viewBox units) visually fixed
  // on screen -- anchor is the canvas center for the zoom buttons, or the
  // cursor position for the scroll wheel. Without this, scale() applied
  // around the SVG's origin corner flings the content toward the
  // bottom-right the moment you zoom in.
  function zoomBy(factor, anchor) {
    setView(({ zoom: z0, pan: p0 }) => {
      const z1 = clampZoom(z0 * factor);
      const ratio = z1 / z0;
      return {
        zoom: z1,
        pan: {
          x: anchor.x - ratio * (anchor.x - p0.x),
          y: anchor.y - ratio * (anchor.y - p0.y),
        },
      };
    });
  }

  function resetView() {
    setView({ zoom: 1, pan: { x: 0, y: 0 } });
  }

  // Converts a real screen-pixel offset into viewBox units, using the
  // SVG's actual rendered size -- these are NOT 1:1 whenever the SVG is
  // displayed at a different pixel size than its logical viewBox (i.e.
  // essentially always), so drag deltas measured in client pixels can't
  // be applied to pan (which lives in viewBox units) without this.
  function pxToViewBoxUnits(dxPx, dyPx) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { dx: dxPx, dy: dyPx };
    return {
      dx: dxPx * (logicalWidth / rect.width),
      dy: dyPx * (logicalHeight / rect.height),
    };
  }

  function clientToViewBoxPoint(clientX, clientY) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: logicalWidth / 2, y: logicalHeight / 2 };
    return {
      x: (clientX - rect.left) * (logicalWidth / rect.width),
      y: (clientY - rect.top) * (logicalHeight / rect.height),
    };
  }

  function handlePointerDown(e) {
    if (!interactive) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, startPan: view.pan, moved: false };
    setIsDragging(true);
  }

  function handlePointerMove(e) {
    if (!dragState.current) return;
    const { dx, dy } = pxToViewBoxUnits(
      e.clientX - dragState.current.startX,
      e.clientY - dragState.current.startY
    );
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragState.current.moved = true;
    setView((v) => ({ ...v, pan: { x: dragState.current.startPan.x + dx, y: dragState.current.startPan.y + dy } }));
  }

  function handlePointerUp() {
    dragState.current = null;
    setIsDragging(false);
  }

  function handleRoomClick(roomLabel) {
    if (dragState.current?.moved) return; // this was a pan, not a click
    onRoomClick?.(roomLabel);
  }

  // One global frame for the whole apartment, computed once -- NOT
  // per-room. Every room's polygon already sits in the same coordinate
  // space as every other room's.
  const [apartmentMinX, apartmentMaxX, apartmentMinY, apartmentMaxY] = boundingBox(
    combination.rooms.map((room) => room.footprintPolygon)
  );
  const apartmentWidth = apartmentMaxX - apartmentMinX;
  const apartmentHeight = apartmentMaxY - apartmentMinY;

  const margin = (showDimensions ? RULER_MARGIN : 0) + STROKE_BLEED_MARGIN;

  let logicalWidth, logicalHeight;
  if (interactive) {
    // Panel-fill: the canvas is exactly the real box the parent panel
    // gave it (see the ResizeObserver above), not a box shaped to match
    // the apartment's own aspect ratio. Fall back to a plausible size
    // before the first measurement lands, to avoid a divide-by-zero.
    logicalWidth = measuredBox?.width || 600;
    logicalHeight = measuredBox?.height || 600;
  } else {
    // Shrink-to-content: unchanged behaviour for comparison-card
    // thumbnails, whose box has no fixed panel to fill.
    const [contentWidth, contentHeight] = canvasContentSize(apartmentWidth, apartmentHeight);
    logicalWidth = contentWidth + margin * 2;
    logicalHeight = contentHeight + margin * 2;
  }

  const drawWidth = logicalWidth - margin * 2;
  const drawHeight = logicalHeight - margin * 2;
  const scale = fitScale(apartmentWidth, apartmentHeight, drawWidth, drawHeight);

  // In shrink-to-content mode this resolves to ~0 (drawWidth/drawHeight
  // already match the apartment's aspect ratio exactly). In panel-fill
  // mode it's real letterboxing whenever the panel's aspect ratio doesn't
  // match the apartment's -- centered on every side, same as before.
  const offsetX = margin + (drawWidth - apartmentWidth * scale) / 2;
  const offsetY = margin + (drawHeight - apartmentHeight * scale) / 2;

  // widthCap only applies in shrink-to-content mode: a cap on HEIGHT is
  // really a cap on WIDTH there, converted via the drawing's own aspect
  // ratio, since height is computed FROM width (see the sx below). In
  // panel-fill mode `maxHeight` is instead applied directly as the
  // wrapper's real CSS height (see wrapper sx) -- there is no derived
  // aspect-ratio cap to compute.
  // Wheel zoom is wired up via a native, non-passive event listener here
  // rather than JSX's onWheel prop. React attaches onWheel as a PASSIVE
  // listener, which means e.preventDefault() inside it is silently
  // ignored by the browser (and Chrome logs a warning about it) -- that's
  // why scrolling over the canvas was also scrolling the page underneath:
  // our preventDefault() call was never actually taking effect.
  // addEventListener('wheel', ..., { passive: false }) is the only way to
  // make preventDefault() here actually stop the page from scrolling.
  useEffect(() => {
    if (!interactive || !svgRef.current) return undefined;
    const el = svgRef.current;

    function onWheelNative(e) {
      e.preventDefault();
      const anchor = clientToViewBoxPoint(e.clientX, e.clientY);
      const factor = e.deltaY < 0 ? 1 + ZOOM_STEP / 3 : 1 / (1 + ZOOM_STEP / 3);
      zoomBy(factor, anchor);
    }

    el.addEventListener('wheel', onWheelNative, { passive: false });
    return () => el.removeEventListener('wheel', onWheelNative);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, logicalWidth, logicalHeight]);

  const aspect = logicalWidth / logicalHeight;
  const widthCap =
    interactive || maxHeight == null
      ? undefined
      : typeof maxHeight === 'number'
        ? maxHeight * aspect
        : Object.fromEntries(Object.entries(maxHeight).map(([bp, v]) => [bp, v * aspect]));

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        width: '100%',
        // Panel-fill mode: give the wrapper a real CSS height so there's
        // something concrete for the ResizeObserver to measure and for
        // the svg below to fill. `maxHeight` (despite its name, historically
        // a cap) becomes that literal height here; falling back to '100%'
        // lets a parent that stretches this box via flex (like the
        // workspace's canvas column) hand down its own height instead.
        ...(interactive ? { height: maxHeight ?? '100%' } : {}),
      }}
    >
      {showZoomControls && (
        <Box sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 1,
          display: 'flex', border: `1px solid ${BORDER}`, borderRadius: '6px',
          overflow: 'hidden', background: '#fff',
        }}>
          <ZoomButton
            label="Zoom out"
            onClick={() => zoomBy(1 / (1 + ZOOM_STEP), { x: logicalWidth / 2, y: logicalHeight / 2 })}
          >
            <ZoomOutIcon sx={{ fontSize: '1rem' }} />
          </ZoomButton>
          <ZoomButton
            label="Zoom in"
            onClick={() => zoomBy(1 + ZOOM_STEP, { x: logicalWidth / 2, y: logicalHeight / 2 })}
            bordered
          >
            <ZoomInIcon sx={{ fontSize: '1rem' }} />
          </ZoomButton>
          <ZoomButton label="Fit to screen" onClick={resetView}>
            <CropFreeIcon sx={{ fontSize: '0.95rem' }} />
          </ZoomButton>
        </Box>
      )}

      <Box
        ref={svgRef}
        component="svg"
        viewBox={`0 0 ${logicalWidth} ${logicalHeight}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        sx={{
          width: '100%',
          // Panel-fill mode: fill the wrapper's real height exactly, rather
          // than 'auto' height derived from content aspect ratio. Content
          // that overflows the viewBox at high zoom is clipped -- SVG does
          // this by default, `overflow: hidden` just makes it explicit/
          // guaranteed rather than left to browser defaults.
          height: interactive ? '100%' : 'auto',
          display: 'block',
          margin: interactive ? 0 : '0 auto',
          overflow: 'hidden',
          ...(widthCap != null ? { maxWidth: widthCap } : {}),
          // Only set an explicit cursor when interactive -- a non-interactive
          // canvas (e.g. inside CombinationCard) should inherit whatever
          // cursor its container already set (typically 'pointer'), not
          // hardcode 'default' and break that.
          ...(interactive ? { cursor: isDragging ? 'grabbing' : 'grab' } : {}),
          touchAction: interactive ? 'none' : 'auto',
        }}
      >
        <defs>
          {/* Diagonal hatch fill for a room with no room-uses at all --
              declared once per canvas (not per room), with a unique id
              per instance (see hatchId) so several floorplans on the same
              page never collide on the same pattern id. */}
          <pattern
            id={hatchId}
            width={HATCH_SIZE} height={HATCH_SIZE}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1={0} y1={0} x2={0} y2={HATCH_SIZE} stroke={HATCH_COLOR} strokeWidth={HATCH_SIZE / 3} />
          </pattern>
        </defs>

        {/* Pan + zoom transform -- wraps only the drawing itself, not the
            ruler, so ruler text/ticks never get visually scaled. */}
        <g transform={`translate(${view.pan.x}, ${view.pan.y}) scale(${view.zoom})`}>
          <g transform={`translate(${offsetX}, ${offsetY})`}>
            {combination.rooms.map((room) => {
              const isSelected = interactive && selectedRoomLabel === room.label;

              // A room with no room-uses at all -- nothing was matched for
              // it on the FRUITS tab, so there's nothing to plan here yet
              // (see koordinator5.py's "no RoomUse defined" case). Shown
              // as a hatched area in BOTH interactive and non-interactive
              // (comparison-card) renderings; the centered label below is
              // shown in both too.
              const hasNoUses = room.roomUses.length === 0;

              // Empty -- the room HAS room-uses to choose from, but no real
              // furnished layout is currently selected. This covers both a
              // resident explicitly clicking the Empty pill and a room
              // nobody has touched yet -- those are deliberately the same
              // state (see RoomUsePanel4.jsx's file header) -- so it gets
              // the dashed outline AND the centered label below, same as a
              // room with no uses at all gets its own label.
              const selectedLayoutOption = getSelectedLayoutOption(room);
              const isEmptySelected = !hasNoUses && !selectedLayoutOption;

              const localFootprint = polygonToLocal(room.footprintPolygon, apartmentMinX, apartmentMinY, apartmentHeight);
              const footprintPathD = polygonToPathD(localFootprint, scale);

              // Same selection-tint logic every room uses, regardless of
              // hasNoUses -- the hatch (below) is a separate layer drawn
              // ON TOP of this, not a replacement for it, so a hatched
              // room still lights up green underneath its diagonal lines
              // when selected.
              const footprintFill = isSelected ? 'rgba(33,131,51,0.06)' : '#fff';

              // Inset inward from the real walls (see geometry2.js's
              // insetRectilinearPolygon) so this dashed line never sits
              // directly on top of -- and obscures -- the real wall lines
              // drawn below it.
              let emptyOutlinePathD = null;
              if (isEmptySelected) {
                const insetFootprint = insetRectilinearPolygon(room.footprintPolygon, EMPTY_OUTLINE_INSET_MM);
                const localInset = polygonToLocal(insetFootprint, apartmentMinX, apartmentMinY, apartmentHeight);
                emptyOutlinePathD = polygonToPathD(localInset, scale);
              }

              // Centered room-label position -- shared by the "no
              // room-uses at all" case and the "Empty selected/default"
              // case, since both get the exact same centered/uppercase
              // treatment. Shown on both the interactive canvas and the
              // compare grid (see roomLabelFontSize below for how the
              // compare grid keeps this legible at its much smaller
              // physical size).
              let roomLabelPos = null;
              if (hasNoUses || isEmptySelected) {
                const centroid = polygonCentroid(room.footprintPolygon);
                const [localCentroidX, localCentroidY] = toLocalPoint(centroid, apartmentMinX, apartmentMinY, apartmentHeight);
                roomLabelPos = [localCentroidX * scale, localCentroidY * scale];
              }

              // See NON_INTERACTIVE_LABEL_FONT_RATIO's own comment for why
              // the compare grid needs a logicalHeight-derived size rather
              // than reusing the interactive canvas's flat constant.
              const roomLabelFontSize = interactive
                ? ROOM_LABEL_FONT_SIZE
                : logicalHeight * NON_INTERACTIVE_LABEL_FONT_RATIO;

              return (
                <g
                  key={room.label}
                  onClick={interactive ? () => handleRoomClick(room.label) : undefined}
                  style={interactive ? { cursor: 'pointer' } : undefined}
                >
                  {/* Footprint fill -- true polygon, not a bounding rect, so an
                      L-shaped room reads as L-shaped, not as a phantom rectangle. */}
                  <path d={footprintPathD} fill={footprintFill} />

                  {/* Hatch overlay -- drawn ON TOP of the fill above, not
                      instead of it, so the pattern's own transparent gaps
                      let the base color (including the selected-green
                      tint) show through between the diagonal lines. */}
                  {hasNoUses && (
                    <path d={footprintPathD} fill={`url(#${hatchId})`} />
                  )}

                  {/* Wall segments, drawn individually so gaps (doorways/openings)
                      in the source data render as real gaps, not a closed loop. */}
                  {room.wallSegments.map((segment, i) => {
                    const localSegment = polygonToLocal(segment, apartmentMinX, apartmentMinY, apartmentHeight);
                    const [[x1, y1], [x2, y2]] = localSegment;
                    return (
                      <line
                        key={i}
                        x1={x1 * scale} y1={y1 * scale}
                        x2={x2 * scale} y2={y2 * scale}
                        stroke={isSelected ? GREEN : CHARCOAL}
                        strokeWidth={interactive ? 2.25 : 2}
                        strokeLinecap="round"
                      />
                    );
                  })}

                  {/* Window openings -- a real grey line along the wall,
                      not just the blank gap that's left where a wall
                      segment was omitted. */}
                  {(room.windowSegments || []).map((segment, i) => {
                    const localSegment = polygonToLocal(segment, apartmentMinX, apartmentMinY, apartmentHeight);
                    const [[x1, y1], [x2, y2]] = localSegment;
                    return (
                      <line
                        key={`window-${i}`}
                        x1={x1 * scale} y1={y1 * scale}
                        x2={x2 * scale} y2={y2 * scale}
                        stroke={WINDOW_COLOR}
                        strokeWidth={interactive ? 2.25 : 2}
                        strokeLinecap="round"
                      />
                    );
                  })}

                  {/* Dashed "Empty layout selected" outline -- inset inward
                      from the real walls so it sits alongside them, never
                      on top of them. */}
                  {emptyOutlinePathD && (
                    <path
                      d={emptyOutlinePathD}
                      fill="none"
                      stroke="rgba(0,0,0,0.4)"
                      strokeWidth={1.2}
                      strokeDasharray="4 3"
                    />
                  )}

                  {/* Centered room label -- shown for a no-room-use room,
                      or a room currently showing as Empty, on BOTH the
                      interactive canvas and the compare grid. Every other
                      room's name is already visible in the Explore tab
                      once a real layout is selected there, so it isn't
                      repeated on the interactive canvas in that case --
                      the compare grid has no such fallback, but showing
                      every room's name there was a bigger change than
                      asked for, so it's scoped the same as the
                      interactive canvas for now. */}
                  {roomLabelPos && (
                    <text
                      x={roomLabelPos[0]}
                      y={roomLabelPos[1]}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={roomLabelFontSize}
                      fontWeight={600}
                      letterSpacing="0.06em"
                      fill="rgba(0,0,0,0.55)"
                      style={{ textTransform: 'uppercase' }}
                    >
                      {room.label}
                    </text>
                  )}

                  {/* Doors, drawn from the room's own geometry -- always
                      present, regardless of whether a layout has been
                      chosen yet (see RoomDoors below). */}
                  <RoomDoors
                    room={room}
                    pieceByName={pieceByName}
                    apartmentMinX={apartmentMinX}
                    apartmentMinY={apartmentMinY}
                    apartmentHeight={apartmentHeight}
                    scale={scale}
                  />

                  <RoomFurniture
                    room={room}
                    pieceByName={pieceByName}
                    apartmentMinX={apartmentMinX}
                    apartmentMinY={apartmentMinY}
                    apartmentHeight={apartmentHeight}
                    scale={scale}
                  />
                </g>
              );
            })}
          </g>
        </g>

        {showDimensions && (
          <Ruler
            offsetX={offsetX}
            offsetY={offsetY}
            scale={scale}
            zoom={view.zoom}
            pan={view.pan}
            logicalWidth={logicalWidth}
            logicalHeight={logicalHeight}
            gutter={margin}
          />
        )}
      </Box>
    </Box>
  );
}

// Affixed ruler along the bottom (width) and left (height) edges. The
// ruler bars themselves are fixed-position, screen-space elements that
// always span the full canvas edge -- they're drawn as siblings of the
// pan/zoom group, not children of it, so they're never scaled or panned
// out of view along with the drawing. The tick marks and mm labels
// inside them are computed from the current pan/zoom/scale state, with
// zero pinned to each bar's own near edge rather than to any fixed point
// in the apartment -- see the header comment and buildSlidingTicks for
// why (the "sliding tape measure" workflow, deliberately different from
// Figma/Illustrator's document-anchored rulers).
function Ruler({ offsetX, offsetY, scale, zoom, pan, logicalWidth, logicalHeight, gutter }) {
  const effectiveScale = scale * zoom;
  const stepMm = chooseTickStepMm(effectiveScale);

  const toScreenX = (mmX) => pan.x + zoom * (offsetX + mmX * scale);
  const toScreenY = (mmY) => pan.y + zoom * (offsetY + mmY * scale);
  // Inverse of the above -- given a screen-space position along a ruler
  // bar, what real-world mm value is currently sitting there. Used to find
  // the full mm range visible along each bar so ticks can run its whole
  // length, not just across the apartment's own extent.
  const mmAtScreenX = (sx) => ((sx - pan.x) / zoom - offsetX) / scale;
  const mmAtScreenY = (sy) => ((sy - pan.y) / zoom - offsetY) / scale;

  // The content region sits inset by `gutter` (really a symmetric margin
  // now, see FloorplanCanvas) on every side -- so the ruler bars span
  // exactly that content region, not the full canvas width/height, which
  // now includes an equal margin on the far side too.
  const contentRight = logicalWidth - gutter;
  const contentBottom = logicalHeight - gutter;

  const bottomLineY = contentBottom + 10;
  const leftLineX = gutter - 10;

  // Sliding zero: 0 is always exactly at the bar's own near edge (left
  // for the horizontal bar, top for the vertical one) -- NOT any fixed
  // point in the apartment. Panning the floorplan is how the user
  // re-zeros it: drag a point of interest to the edge, then read the
  // distance to anything else directly off the far end, no subtraction
  // needed (see buildSlidingTicks).
  const xEdgeMm = mmAtScreenX(gutter);
  const xSpanMm = mmAtScreenX(contentRight) - xEdgeMm;
  const xTicks = [];
  for (const { offsetMm, label } of buildSlidingTicks(xSpanMm, stepMm)) {
    xTicks.push({ sx: toScreenX(xEdgeMm + offsetMm), label, isZero: offsetMm === 0 });
  }

  const yEdgeMm = mmAtScreenY(gutter);
  const ySpanMm = mmAtScreenY(contentBottom) - yEdgeMm;
  const yTicks = [];
  for (const { offsetMm, label } of buildSlidingTicks(ySpanMm, stepMm)) {
    yTicks.push({ sy: toScreenY(yEdgeMm + offsetMm), label, isZero: offsetMm === 0 });
  }

  return (
    <>
      <line x1={0} y1={bottomLineY} x2={logicalWidth} y2={bottomLineY} stroke={MUTED_LINE} strokeWidth={1} />
      {xTicks.map(({ sx, label, isZero }) => (
        <g key={`x-${sx.toFixed(2)}`}>
          <line
            x1={sx} y1={bottomLineY - (isZero ? 6 : 4)} x2={sx} y2={bottomLineY + (isZero ? 6 : 4)}
            stroke={isZero ? CHARCOAL : MUTED_LINE} strokeWidth={isZero ? 1.5 : 1}
          />
          <text x={sx} y={bottomLineY + 16} textAnchor="middle" fontSize={8.5} fontWeight={isZero ? 700 : 400} fill={isZero ? CHARCOAL : MUTED_LINE}>
            {label}
          </text>
        </g>
      ))}

      <line x1={leftLineX} y1={0} x2={leftLineX} y2={logicalHeight} stroke={MUTED_LINE} strokeWidth={1} />
      {yTicks.map(({ sy, label, isZero }) => (
        <g key={`y-${sy.toFixed(2)}`}>
          <line
            x1={leftLineX - (isZero ? 6 : 4)} y1={sy} x2={leftLineX + (isZero ? 6 : 4)} y2={sy}
            stroke={isZero ? CHARCOAL : MUTED_LINE} strokeWidth={isZero ? 1.5 : 1}
          />
          <text
            x={leftLineX - 8}
            y={sy}
            textAnchor="middle"
            fontSize={8.5}
            fontWeight={isZero ? 700 : 400}
            fill={isZero ? CHARCOAL : MUTED_LINE}
            transform={`rotate(-90 ${leftLineX - 8} ${sy})`}
          >
            {label}
          </text>
        </g>
      ))}

      <text x={gutter - 6} y={logicalHeight - 8} fontSize={7.5} fill={MUTED_LINE} textAnchor="end">mm</text>
    </>
  );
}

function ZoomButton({ children, label, onClick, bordered = false }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fff', border: 'none',
        borderLeft: bordered ? `1px solid ${BORDER}` : 'none',
        borderRight: bordered ? `1px solid ${BORDER}` : 'none',
        cursor: 'pointer', color: 'rgba(0,0,0,0.65)',
        '&:hover': { background: '#f5f5f4', color: CHARCOAL },
      }}
    >
      {children}
    </Box>
  );
}

// ─── RoomDoors ──────────────────────────────────────────────────────────────
// Draws every door in room.doorPlacements -- computed once, at the room's
// own geometry level, independent of any chosen layout (see
// RoomGeometry.doorPlacementsJson / DoorPlacementDto on the backend). This
// is why doors show up on a bare, unfurnished floor plan instead of only
// appearing once a layout has been picked.
//
// Reuses the exact same rendering approach as RoomFurniture
// (placementToLocalRect + an <image>), since a door placement has the
// same shape (furniturePieceName/startPoint/rotation) as a real furniture
// placement. If a layout is later chosen for this room, that layout's own
// door placement (added by the pipeline's compute_doors() to every
// layout) draws on top at the identical position/rotation -- a harmless,
// invisible overlap rather than a visible duplicate.
function RoomDoors({ room, pieceByName, apartmentMinX, apartmentMinY, apartmentHeight, scale }) {
  const doorPlacements = room.doorPlacements || [];
  if (!doorPlacements.length) return null;

  return (
    <>
      {doorPlacements.map((placement, i) => {
        const piece = pieceByName[placement.furniturePieceName];
        if (!piece) return null; // unknown stencil name -- skip rather than render a broken image

        const rect = placementToLocalRect(piece, placement, apartmentMinX, apartmentMinY, apartmentHeight, scale);

        return (
          <image
            key={`door-${i}`}
            href={piece.imageUrl}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            transform={rect.transform}
            preserveAspectRatio="none"
          />
        );
      })}
    </>
  );
}

function RoomFurniture({ room, pieceByName, apartmentMinX, apartmentMinY, apartmentHeight, scale }) {
  const layoutOption = getSelectedLayoutOption(room);
  if (!layoutOption || !layoutOption.placements?.length) return null;

  return (
    <>
      {layoutOption.placements.map((placement) => {
        const piece = pieceByName[placement.furniturePieceName];
        if (!piece) return null; // unknown stencil name -- skip rather than render a broken image

        // Deliberately computed from piece.nativeWidthMm/nativeHeightMm +
        // placement.startPoint/rotation, NOT from placement.footprintPolygon
        // -- see geometry.js's placementToLocalRect for why the footprint
        // can't be used directly as a rendering input.
        const rect = placementToLocalRect(piece, placement, apartmentMinX, apartmentMinY, apartmentHeight, scale);

        return (
          <image
            key={placement.id}
            href={piece.imageUrl}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            transform={rect.transform}
            preserveAspectRatio="none"
          />
        );
      })}
    </>
  );
}